import { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  Wallet,
  ShoppingCart,
  CreditCard,
  Building2,
  DollarSign,
  PiggyBank,
  Plus,
} from 'lucide-react';
import { getDashboardDataApi } from '../api/api';
import { AuthContext } from '../context/AuthContext.jsx';
import { useCompany } from '../context/CompanyContext.jsx';
import { useOrganizationSettings } from '../context/OrganizationSettingsContext';
import Card from '../components/ui/Card.jsx';
import Button from '../components/ui/Button.jsx';
import './Dashboard.css';

export default function Dashboard() {
  const { user } = useContext(AuthContext)
  const { settings } = useOrganizationSettings();
  const { companyId } = useCompany();
  const [dashboardData, setDashboardData] = useState(false);
  const [totalBillAmount, setTotalBillAmount] = useState(0);
  const [totalPaidAmount, setTotalPaidAmount] = useState(0);
  const [totalRemainingAmount, setTotalRemainingAmount] = useState(0);
  const [openQuick, setOpenQuick] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [error, setError] = useState('')
  const toggleExpand = (id) => setExpanded((prev) => (prev === id ? null : id));
  const nav = useNavigate()

  useEffect(() => {
      const params = {};
      // Company filtering (Case 3 & 4) - use global company from context
      if (settings.userWise && companyId) {
        params.companyId = companyId;
      }
      getDashboardDataApi(params)
        .then((res) => {
          setDashboardData(res.data?.data || res.data)
          setTotalBillAmount(res.data?.totalBillAmount || 0);
          setTotalPaidAmount(res.data?.totalPaidAmount || 0);
          setTotalRemainingAmount(res.data?.totalRemainingAmount || 0);
        }
      ).catch(()=>setError('Failed to load dashboard data'))
  }, [companyId, settings.userWise]);

  const setOpenPage = () =>{
    nav('/receipts');
  }

  const formatCurrency = (number, locale = 'en-IN', currency = 'INR') => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0,
    }).format(number || 0);
  };

  let stats = {
    Income: [
      { id: "sales", title: "Sales", value: formatCurrency(dashboardData.sales), icon: <TrendingUp size={20} /> },
      { id: "receipt", title: "Receipt", value: formatCurrency(dashboardData.receipt), icon: <Wallet size={20} /> },
    ],
    Expense: [
      { id: "purchase", title: "Purchase", value: formatCurrency(dashboardData.purchase), icon: <ShoppingCart size={20} /> },
      { id: "payment", title: "Payment", value: formatCurrency(dashboardData.payment), icon: <CreditCard size={20} /> },
    ],
    Balances: [
      { id: "outstanding", title: "Outstanding", value: formatCurrency(dashboardData.outstandingBalance), icon: <Building2 size={20} /> },
      { id: "cash", title: "Cash Balance", value: formatCurrency(dashboardData.cashBalance), icon: <DollarSign size={20} /> },
      { id: "bank", title: "Bank Balance", value: formatCurrency(dashboardData.bankBalance), icon: <PiggyBank size={20} /> },
    ],
  };

 if(user?.isOwner == false){
   stats = {
    Income: [
      { id: "outstanding", title: "Outstanding", value: formatCurrency(totalRemainingAmount), icon: <DollarSign size={20} /> },
      // { id: "remaining", title: "Remaining", value: formatCurrency(totalRemainingAmount), icon: <DollarSign size={20} /> },
      { id: "todayReceipt", title: "Today Receipt", value: formatCurrency(totalPaidAmount), icon: <DollarSign size={20} /> },
    ],
    Expense: [],
    Balances: []
  }
}

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-error">{error}</div>
      </div>
    );
  }

  const allStats = [
    ...stats.Income,
    ...stats.Expense,
    ...stats.Balances,
  ];

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h2>Dashboard</h2>
      </div>

      {allStats.length === 0 ? (
        <div className="dashboard-empty">No records available</div>
      ) : (
        <>
          {stats.Income.length > 0 && (
            <div className="dashboard-section">
              <h6 className="dashboard-section-header">Income</h6>
              <div className="dashboard-stats">
                {stats.Income.map((item) => (
                  <div
                    key={item.id}
                    className="dashboard-stat-card"
                    onClick={() => toggleExpand(item.id)}
                  >
                    <div className="dashboard-stat-card-body">
                      <div className="dashboard-stat-content">
                        <p className="dashboard-stat-title">{item.title}</p>
                        <h4 className="dashboard-stat-value">{item.value}</h4>
                      </div>
                      <div className="dashboard-stat-icon">{item.icon}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stats.Expense.length > 0 && (
            <div className="dashboard-section">
              <h6 className="dashboard-section-header">Expense</h6>
              <div className="dashboard-stats">
                {stats.Expense.map((item) => (
                  <div
                    key={item.id}
                    className="dashboard-stat-card"
                    onClick={() => toggleExpand(item.id)}
                  >
                    <div className="dashboard-stat-card-body">
                      <div className="dashboard-stat-content">
                        <p className="dashboard-stat-title">{item.title}</p>
                        <h4 className="dashboard-stat-value">{item.value}</h4>
                      </div>
                      <div className="dashboard-stat-icon">{item.icon}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stats.Balances.length > 0 && (
            <div className="dashboard-section">
              <h6 className="dashboard-section-header">Balances</h6>
              <div className="dashboard-stats">
                {stats.Balances.map((item) => (
                  <div key={item.id} className="dashboard-stat-card">
                    <div className="dashboard-stat-card-body">
                      <div className="dashboard-stat-content">
                        <p className="dashboard-stat-title">{item.title}</p>
                        <h4 className="dashboard-stat-value">{item.value}</h4>
                      </div>
                      <div className="dashboard-stat-icon">{item.icon}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Quick Entry Button */}
      <Button
        variant="info"
        className="dashboard-quick-entry"
        onClick={setOpenPage}
      >
        <Plus size={18} />
        <span className="hide-mobile">Quick Entry</span>
        <span className="show-mobile-only">Add</span>
      </Button>
    </div>
  );
}
